/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/logo.png";
import heroAirport from "@/assets/images/hero-airport.jpg";
import missionAircraft from "@/assets/images/mission-aircraft.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo
  "/images/logo.png": logo,
  "/assets/images/logo.png": logo,
  "https://static.wixstatic.com/media/fd6346_2797cd94ae9749769b66cb7c298f6f66~mv2_d_3520_1462_s_2.png/v1/fill/w_156,h_65,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Untitled.png": logo,
  
  // Hero Airport Image
  "/images/hero-airport.jpg": heroAirport,
  "/assets/images/hero-airport.jpg": heroAirport,
  "https://static.wixstatic.com/media/fd6346_ff4142593782454d8104adde13403ab9~mv2_d_4000_3000_s_4_2.jpg/v1/fill/w_1280,h_720,fp_0.50_0.50,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/fd6346_ff4142593782454d8104adde13403ab9~mv2_d_4000_3000_s_4_2.jpg": heroAirport,
  
  // Mission Section Aircraft Image
  "/images/mission-aircraft.jpg": missionAircraft,
  "/assets/images/mission-aircraft.jpg": missionAircraft,
  "https://static.wixstatic.com/media/fd6346_8212adcad018445aafddec4d45a78f77~mv2_d_4676_3117_s_4_2.jpg/v1/fill/w_640,h_580,fp_0.50_0.50,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/fd6346_8212adcad018445aafddec4d45a78f77~mv2_d_4676_3117_s_4_2.jpg": missionAircraft,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
